import React from 'react';
import LegalPage from './LegalPage-new';

function PrivacyPolicyPage(): React.ReactElement
{
    return (
        <LegalPage
            eyebrow="Privacy Policy"
            title="How AcceliQA collects, uses, and protects data"
            summary="This policy explains what information we process when you create an account, connect repositories, upload requirements, and use AcceliQA to generate testing assets and workflow insights."
            lastUpdated="April 3, 2026"
            sections={[
                {
                    id: 'information-we-collect',
                    title: 'Information We Collect',
                    content: (
                        <>
                            <p>
                                We collect information you provide directly, such as account registration details, profile information, billing contacts, support requests, repository configuration, project requirements, and uploaded documents.
                            </p>
                            <ul>
                                <li>Identity and contact data, including name, email address, organization, and role.</li>
                                <li>Workspace content, such as repositories, requirement artifacts, prompts, and generated outputs.</li>
                                <li>Usage and diagnostic data, including session activity, API consumption, and operational logs.</li>
                            </ul>
                        </>
                    )
                },
                {
                    id: 'how-we-use-data',
                    title: 'How We Use Data',
                    content: (
                        <>
                            <p>
                                We use collected data to provide the service, authenticate users, generate tests and reports, maintain security, respond to support requests, administer billing, and improve platform performance and reliability.
                            </p>
                            <p>
                                We do not use your private repository or requirements data for unrelated marketing purposes. Where we use aggregated or de-identified data for analytics or service improvement, we take steps designed to avoid identifying your organization or proprietary content.
                            </p>
                        </>
                    )
                },
                {
                    id: 'sharing-and-disclosures',
                    title: 'Sharing and Disclosures',
                    content: (
                        <>
                            <p>
                                We share information only as necessary to operate the service, comply with law, protect rights and security, or at your direction. This may include cloud hosting providers, subprocessors that support core platform functionality, payment processors, and integration partners you enable.
                            </p>
                            <p>
                                We do not sell personal information. We require service providers to handle data in accordance with contractual confidentiality and security obligations.
                            </p>
                        </>
                    )
                },
                {
                    id: 'retention',
                    title: 'Retention and Deletion',
                    content: (
                        <>
                            <p>
                                We retain data for as long as needed to provide the service, comply with legal obligations, resolve disputes, enforce agreements, and maintain security or audit trails. Retention periods vary based on the type of data and your subscription settings.
                            </p>
                            <p>
                                When data is no longer required, we delete or de-identify it in accordance with our retention processes, subject to backup, legal hold, and security requirements.
                            </p>
                        </>
                    )
                },
                {
                    id: 'security',
                    title: 'Security Measures',
                    content: (
                        <>
                            <p>
                                We use administrative, technical, and physical safeguards designed to protect customer data against unauthorized access, disclosure, alteration, and destruction. These measures may include encryption in transit, access controls, logging, environment segregation, and routine operational monitoring.
                            </p>
                            <p>
                                No system is completely secure, so you should also protect credentials, configure least-privilege access, and avoid uploading data beyond what is necessary for your use case.
                            </p>
                        </>
                    )
                },
                {
                    id: 'your-rights',
                    title: 'Your Choices and Rights',
                    content: (
                        <>
                            <p>
                                Depending on your jurisdiction, you may have rights to access, correct, delete, or export certain personal information, and to object to or restrict certain processing. Organization administrators may also manage user profiles and project data within their workspace.
                            </p>
                            <p>
                                To exercise applicable privacy rights or submit a data request, contact us using the details below. We may need to verify your identity and organizational authority before fulfilling a request.
                            </p>
                        </>
                    )
                },
                {
                    id: 'contact',
                    title: 'Contact Us',
                    content: (
                        <>
                            <p>
                                For privacy questions, security inquiries, or requests related to personal data, contact Accelirate at contact@accelirate.com.
                            </p>
                            <p>
                                If we make material changes to this policy, we will update the effective date above and, where appropriate, provide additional notice inside the application or through your account contact.
                            </p>
                        </>
                    )
                }
            ]}
            footerNote="This page is intended to explain our general privacy practices for AcceliQA. If your organization has a separate data processing agreement or enterprise contract with Accelirate, that agreement may supplement or override portions of this policy for the covered services."
        />
    );
}

export default PrivacyPolicyPage;